// src/components/FolderContentArea/DocumentPreviewPanel.jsx
//
// A right-side overlay panel that slides in over the table content.
// Uses position:absolute so it covers the table without affecting layout.
// The parent container (folder-content-area--with-preview) must have
// position:relative and overflow:hidden to scope and clip the panel.
import React from 'react';
import { useIntl } from 'react-intl';
import { FlexGroup, Icon, Skeleton, Text, Tooltip } from '@zenqms/zui';
import { PDFtron } from '../../document/PDFTron';
import './DocumentPreviewPanel.scss';

/**
 * @param {boolean}      open          — whether the panel is visible
 * @param {Function}     onClose       — called when the user closes the panel
 * @param {string}       documentName  — shown in the panel header
 * @param {string}       url           — pre-signed S3 URL passed to PDFtron
 * @param {boolean}      isLoading     — shows skeleton while URL is being fetched
 * @param {string|null}  fileExtension — the revision's extension as the pdf-url endpoint reports it
 *                                       (e.g. 'pdf', 'mp4'), falling back to the one in the URL when
 *                                       the response has none. PDFtron handles both PDF and video
 *                                       formats natively; passing the correct extension ensures the
 *                                       right viewer mode is activated (PDF viewer vs video viewer).
 * @param {boolean}      [noRevision]  — the document exists but has no revision attached (e.g. a draft),
 *                                       so there is nothing to render. Shows a message instead of a
 *                                       blank viewer; `url` is null in this case.
 * @param {{ message: string, position: 'left'|'top'|'bottom'|'center' }|null} [watermarkOptions]
 *                                     — what to draw over a video, as returned by the pdf-url endpoint.
 *                                       Null for a PDF (its watermark is already burned in), for a
 *                                       category without a watermark, and for files. Pass the response's
 *                                       own object through rather than rebuilding it, so its identity
 *                                       stays steady across re-renders.
 */
export function DocumentPreviewPanel({
  open,
  onClose,
  documentName,
  url,
  isLoading,
  fileExtension,
  noRevision = false,
  watermarkOptions,
}) {
  const { formatMessage } = useIntl();

  return (
    <>
      {/* Transparent backdrop — captures clicks outside the panel */}
      <div
        className="doc-preview-panel__backdrop"
        data-open={open ? 'true' : 'false'}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="doc-preview-panel"
        data-open={open ? 'true' : 'false'}
        role="complementary"
        aria-label={formatMessage({ id: 'documentPreviewPanel.ariaLabel', defaultMessage: 'Document preview' })}
        aria-hidden={!open}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <FlexGroup alignItems="center" justifyContent="space-between" className="doc-preview-panel__header">
          <Text as="span" weight="bold" size="md" className="doc-preview-panel__title">
            {documentName || formatMessage({ id: 'documentPreviewPanel.title', defaultMessage: 'Document Preview' })}
          </Text>
          <Tooltip
            content={formatMessage({ id: 'documentPreviewPanel.close', defaultMessage: 'Close preview' })}
            position="left center"
            on={['hover', 'focus']}
            trigger={
              <button
                type="button"
                className="doc-preview-panel__close-btn"
                onClick={onClose}
                aria-label={formatMessage({ id: 'documentPreviewPanel.close', defaultMessage: 'Close preview' })}
              >
                <Icon name="xmark" noHover />
              </button>
            }
          />
        </FlexGroup>

        {/* ── Viewer area ─────────────────────────────────────────────────── */}
        <div className="doc-preview-panel__viewer">
          {isLoading && (
            <FlexGroup direction="column" gap="sm" className="doc-preview-panel__loading">
              <Skeleton variant="rectangular" height={32} />
              <Skeleton variant="rectangular" height="100%" />
            </FlexGroup>
          )}
          {!isLoading && url && open && (
            /* Key on url so PDFtron fully remounts when a different document is selected.
               fileExtension drives PDFtron's internal routing: 'pdf' → PDF viewer,
               'mp4'/'ogg'/'webm' → video viewer. */
            <PDFtron
              key={url}
              url={url}
              fileExtension={fileExtension ?? 'pdf'}
              watermarkOptions={watermarkOptions ?? undefined}
            />
          )}
          {!isLoading && open && noRevision && (
            <div className="doc-preview-panel__empty" role="status">
              <Icon name="file" size="xl" noHover color="var(--zui-color-gray-400)" />
              <Text size="md">
                {formatMessage({
                  id: 'documentPreviewPanel.noRevision',
                  defaultMessage: "Document doesn't have a revision uploaded",
                })}
              </Text>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
